import type { Key } from "react-aria-components";
import {
  Collection,
  ListBoxLoadMoreItem,
  ListBox as RAListBox,
  ListBoxItem as RAListBoxItem,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

import CustomCheckbox from "../CustomCheckbox";
import Spinner from "../Spinner";
import type { ExtractValues, SelectItem, SlotClassNames } from "../types";
import type { LabelFormatterFn } from "./utils";

type SelectListboxBaseProps<K extends Key, T extends SelectItem<K>> = {
  readonly items: T[] | readonly T[];
  readonly className?: string;
  readonly classNames?: SlotClassNames<{ option: string }>;
  /** optional only where a surrounding field already labels the listbox */
  readonly "aria-label"?: string;
  readonly labelFormatter?: LabelFormatterFn;
  readonly renderEmptyState?: () => React.ReactNode;
  readonly onLoadMore?: () => void;
  readonly isLoadingMore?: boolean;
};

type SelectListboxActionProps<T extends SelectItem<Key>> = {
  onAction: (key: ExtractValues<NoInfer<T>[]>) => void;
  selectionMode?: never;
  selectedKeys?: never;
  onSelectionChange?: never;
};

type SelectListboxSelectionProps<K extends Key> = {
  onAction?: never;
  selectionMode?: "single" | "multiple";
  selectedKeys?: Iterable<K>;
  onSelectionChange?: (keys: Set<K> | "all") => void;
};

export type SelectListboxProps<
  K extends Key,
  T extends SelectItem<K>,
> = SelectListboxBaseProps<K, T> &
  (SelectListboxActionProps<T> | SelectListboxSelectionProps<K>);

export function SelectListbox<K extends Key, T extends SelectItem<K>>(
  props: SelectListboxProps<K, T>,
) {
  const {
    items,
    onAction,
    className,
    classNames,
    labelFormatter,
    renderEmptyState,
    onLoadMore,
    isLoadingMore = false,
    "aria-label": ariaLabel,
    selectionMode = "single",
    selectedKeys,
    onSelectionChange,
  } = props;

  return (
    <RAListBox<T>
      aria-label={ariaLabel}
      className={twMerge(
        `scroll-area bg-secondary text-text-primary max-h-128 min-h-0 [scrollbar-gutter:stable] space-y-1 overflow-x-hidden overflow-y-auto rounded-md p-2 focus:outline-0`,
        className,
      )}
      renderEmptyState={renderEmptyState}
      selectionMode={selectionMode}
      selectionBehavior={selectionMode === "multiple" ? "toggle" : undefined}
      onAction={
        onAction ? (key) => onAction(key as ExtractValues<T[]>) : undefined
      }
      selectedKeys={selectedKeys ? new Set(selectedKeys) : undefined}
      onSelectionChange={
        onSelectionChange
          ? (keys) => onSelectionChange(keys as Set<K> | "all")
          : undefined
      }
    >
      <Collection items={items}>
        {(item) => (
          <RAListBoxItem
            key={item.value}
            id={item.value}
            isDisabled={item.disabled}
            data-has-description={!!item.description}
            className={twMerge(
              `group/item hover:border-border hover:bg-tertiary flex cursor-pointer items-center gap-3 rounded-lg border border-transparent px-3 py-2 focus:outline-0 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-40 data-[disabled=true]:hover:border-transparent data-[disabled=true]:hover:bg-transparent data-[has-description=true]:py-3`,
              item.color === "danger" && "text-danger",
              classNames?.option,
            )}
            textValue={item.label}
          >
            {({ isSelected }) =>
              item.renderContent ? (
                item.renderContent()
              ) : (
                <>
                  {item.iconPosition !== "right" && item.icon}
                  <p className="mr-auto flex flex-col gap-1 truncate">
                    <span slot="title">
                      {labelFormatter ? labelFormatter(item.label) : item.label}
                    </span>
                    {item.description && (
                      <span
                        slot="description"
                        className="text-text-secondary text-sm"
                      >
                        {item.description}
                      </span>
                    )}
                  </p>
                  {item.badge}
                  {item.iconPosition === "right" && <span>{item.icon}</span>}
                  <CustomCheckbox
                    isSelected={isSelected}
                    className={
                      selectionMode === "multiple"
                        ? "pointer-events-none"
                        : "pointer-events-none hidden group-data-[selected=true]/item:block"
                    }
                  />
                </>
              )
            }
          </RAListBoxItem>
        )}
      </Collection>
      {onLoadMore && (
        <ListBoxLoadMoreItem
          // RAC fires onLoadMore while loading too; one page at a time
          onLoadMore={() => {
            if (!isLoadingMore) onLoadMore();
          }}
          isLoading={isLoadingMore}
          className="flex justify-center py-2"
        >
          <Spinner size="sm" />
        </ListBoxLoadMoreItem>
      )}
    </RAListBox>
  );
}
